/** @jsx jsx */
import { jsx } from "@emotion/core"
import React, { useRef } from "react"
import { IRecord } from "../types/schema"
import { FormState } from "@cd2/forms/build/es6/Form2/FormState"
import { useSiteAlerts } from "../hooks/useSiteAlerts"
import { useSkeem } from "../providers/SkeemProvider"
import { IComponentProp } from "@cd2/tools/build/es6"
import { MultiButton } from "@cd2/components/build/es6"
import { useEvents } from "../utils/useEvents"
import { formEvents } from "@cd2/forms/build/es6/Form2"
import { Form2 } from "@cd2/forms/build/es6"
import { SubmitButton } from "../interfaceParts/SubmitButton"
import { IReminder } from "../pages/user/UserRemindersIndexPage"
import { AllUserSelect } from "../pages/user/UserResultsShowPage/UserSelectForFlowFileApproval"
import { FileField } from "./parts/FileField"
import { useGlobalState } from "../utils/globalState"
import { useAsyncCallback } from "../hooks/useAsyncCallback"
import { messageFromError } from "../utils/error"

interface INewReminderFormProps {
  onAfterSubmit(reminder: IRecord<"reminders", "body" | "date" | "data">): void
  reminderId?: string
  initialData?: {
    date?: Date
    body?: string
    data?: Record<string, any>
    flowFile?: IRecord<"flowFiles">
    flowResult?: IRecord<"flowResults">
    resultId?: string
    userId?: string
    flowResultFileId?: string
    fileboyFileId?: string
  }
  additionalButtons?: IComponentProp<typeof MultiButton, "buttons">
  showUserInput?: boolean
  noMicrosoft?: boolean
  noFileUpload?: boolean
}
interface IFormData {
  date: Date
  body: string
  data?: Record<string, any>
  flowFile?: IRecord<"flowFiles">
  flowResult?: IRecord<"flowResults">
  resultId?: string
  flowResultFileId?: string
  fileboyFileId?: string
  // Might be present if showUserInput is true
  userId?: string
}

export function NewReminderForm({
  onAfterSubmit,
  initialData = {},
  additionalButtons,
  showUserInput,
  noFileUpload,
  reminderId,
  noMicrosoft: _noMicrosoft,
}: INewReminderFormProps) {
  const formRef = useRef<FormState | null>(null)
  const alert = useSiteAlerts()
  const skeem = useSkeem()
  const events = useEvents()
  const globalState = useGlobalState()

  const handleSubmit = useAsyncCallback(async (data: IFormData) => {
    formRef.current?.events.trigger(formEvents.beforeSubmit)
    if (Object.values(formRef.current?.state.errors ?? {}).filter(Boolean).length > 0) {
      alert.genericError("An error occurred. Check the form and try again.")
      return
    }
    try {
      const resultId =
        data.resultId ?? data.flowResult?.id ?? initialData.resultId ?? initialData.flowResult?.id
      const payload = {
        date: data.date,
        body: data.body,
        data: data.data ?? initialData.data ?? {},
        user: { set: data.userId ?? globalState.user.id },
        flowFile: data.flowFile ? { set: data.flowFile.id } : undefined,
        flowResultFiles: data.flowResultFileId ? { set: data.flowResultFileId } : undefined,
        fileboyFileId: data.fileboyFileId,
        ...(resultId ? { result: { set: resultId } } : {}),
      }

      const isEditing = !!reminderId

      let record: IRecord<"reminders", "date" | "body" | "data">
      if (isEditing) {
        record = await skeem.mutate<IReminder>({
          reminders: { update: { id: reminderId, ...payload } },
        })
      } else {
        record = await skeem.mutate<IRecord<"reminders", "date" | "body" | "data">>({
          reminders: { create: payload },
        })
      }

      if (!isEditing && resultId) {
        await events.createReminder(resultId, record.id, payload.date, payload.body)
      }
      alert.createAlert("Reminder")
      onAfterSubmit?.(record)
    } catch (e) {
      console.error(e)
      alert.genericError(`Reminder: ${messageFromError(e)}`)
    }
  })

  return (
    <Form2.Form xRef={formRef} onSubmit={data => handleSubmit(data)} initialData={initialData}>
      <React.Fragment>
        {showUserInput && (
          <Form2.WithField<string>
            field="userId"
            render={field => (
              <AllUserSelect value={field.getValue()} onChange={user => field.setValue(user.id)} />
            )}
          />
        )}
        <Form2.TextArea field="body" validations={{ presence: true }} rows="auto" />
        <Form2.Date
          label="When"
          field="date"
          dateFormat="dd/MM/yyyy HH:mm"
          minDate={new Date()}
          shouldCloseOnSelect={false}
          showTimeInput
        />
        {!noFileUpload && <FileField field="fileboyFileId" label="Upload a file (optional)" />}
        <MultiButton
          allowWrap={true}
          buttons={[() => <SubmitButton value="Create task" />, ...(additionalButtons ?? [])]}
        />
      </React.Fragment>
    </Form2.Form>
  )
}
